import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { PageToolbar, FilterChip } from '@/components/ui/page-toolbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { PhoneField } from '@/components/ui/money-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const PARTY_TYPES = [
  { value: 'shipper', label: 'Shipper' },
  { value: 'consignee', label: 'Consignee' },
  { value: 'both', label: 'Both (Shipper & Consignee)' },
];

const emptyForm = { name: '', type: 'shipper', email: '', phone: '', address: '' };

export default function PartiesPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  const partiesQ = useQuery({
    queryKey: ['shippers', filter],
    queryFn: async () => {
      const q = filter === 'all' ? '' : `?partyType=${encodeURIComponent(filter)}`;
      const r = await api(`/api/v1/shippers${q}`);
      if (r.status !== 200) throw new Error(r.data?.error ?? 'Failed to load parties');
      return r.data.shippers ?? [];
    },
  });

  const parties = partiesQ.data ?? [];
  const deleteTarget = parties.find((p) => p.id === deleteId);

  const openCreate = () => {
    setEditingId('');
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name ?? '',
      type: row.kind || 'shipper',
      email: row.contactEmail ?? '',
      phone: row.contactPhone ?? '',
      address: row.address ?? '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.type) {
      toast.error('Name and type are required');
      return;
    }
    if (!form.email?.trim() || !form.phone?.trim()) {
      toast.error('Email and phone are required');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        partyType: form.type,
        address: form.address || undefined,
        contactEmail: form.email.trim(),
        contactPhone: form.phone.trim(),
      };
      const r = editingId
        ? await api(`/api/v1/shippers/${editingId}`, { method: 'PATCH', body })
        : await api('/api/v1/shippers', { method: 'POST', body });
      if (r.status === 200 || r.status === 201) {
        toast.success(editingId ? 'Party updated' : `Party ${form.name} added`);
        setForm(emptyForm);
        setEditingId('');
        setShowForm(false);
        qc.invalidateQueries({ queryKey: ['shippers'] });
      } else {
        toast.error(String(r.data?.error ?? 'Save failed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const r = await api(`/api/v1/shippers/${deleteId}`, { method: 'DELETE' });
    if (r.status === 200) {
      toast.success('Party removed');
      setDeleteId('');
      qc.invalidateQueries({ queryKey: ['shippers'] });
    } else {
      toast.error(String(r.data?.error ?? 'Delete failed'));
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'kind', label: 'Type', render: (v) => <StatusPill status={v} /> },
    {
      key: 'contactEmail',
      label: 'Email',
      render: (v) => v || '—',
    },
    {
      key: 'contactPhone',
      label: 'Phone',
      className: 'font-mono',
      render: (v) => v || '—',
    },
    {
      key: 'address',
      label: 'Address',
      render: (v) => v || '—',
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex items-center gap-1 justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              openEdit(row);
            }}
            data-testid={`edit-party-${row.id}`}
          >
            <Icons.Pencil className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(row.id);
            }}
            data-testid={`delete-party-${row.id}`}
          >
            <Icons.Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Parties"
        description="All partners live here — shipper, consignee, or both. There is no separate Consignees page; every party is stored with a type on this list and used when building loads."
        icon="Users"
        metrics={[
          { label: 'Total', value: parties.length },
          { label: 'Shippers', value: parties.filter((p) => p.kind === 'shipper').length },
          { label: 'Consignees', value: parties.filter((p) => p.kind === 'consignee').length },
          { label: 'Both', value: parties.filter((p) => p.kind === 'both').length },
        ]}
        actions={
          <Button onClick={openCreate} data-testid="add-party-button">
            <Icons.Plus className="h-4 w-4 mr-2" />
            Add Party
          </Button>
        }
      />

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Party' : 'New Party'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FloatingField
                label="Company Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="party-name-input"
              />
              <div className="space-y-2">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Party Type <span className="text-destructive">*</span>
                </label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="party-type-select">
                    <SelectValue placeholder="Select party type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <FloatingField
                label="Contact Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <PhoneField
                label="Contact Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                required
              />
              <div className="md:col-span-2">
                <FloatingField
                  label="Address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setEditingId('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-party-button">
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add Party'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <PageToolbar>
        {['all', 'shipper', 'consignee', 'both'].map((f) => (
          <FilterChip key={f} active={filter === f} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f === 'both' ? 'Both' : f.charAt(0).toUpperCase() + f.slice(1)}
          </FilterChip>
        ))}
      </PageToolbar>

      <DataTable
        columns={columns}
        data={parties}
        emptyMessage={partiesQ.isLoading ? 'Loading…' : 'No parties yet — add a shipper or consignee to book loads'}
      />

      <ConfirmDialog
        open={Boolean(deleteId)}
        onOpenChange={(open) => !open && setDeleteId('')}
        title="Remove party?"
        description={`Delete ${deleteTarget?.name || 'this party'}? Loads that already reference them keep historical names, but they will leave pickers.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
      />
    </div>
  );
}
